import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { App } from 'antd';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { isContestByTradingSystem } from '@hub/utils/contest-utils';
import { Icons } from '@monorepo/icons';
import { SystemIcon } from '@monorepo/nova-icons';
import { Button, Flex, TextComponent } from '@monorepo/react-components';
import { useResponsiveness } from '@monorepo/react-hooks';
import {
  CredentialsImpersonationRoles,
  ThemeColors,
  TradingSystem,
} from '@monorepo/types';
import { IconsNames } from '@monorepo/types';
import { ThemeWrapper } from '../../components/theme-wrapper/theme-wrapper';
import { useStore } from '../../helpers/use-store';
import withImpersonation from '../../HOC/withImpersonation';
import { useColor } from '../../hooks';
import TradersLogin from '../traders-login/traders-login';
import { NovaCredentialsModal } from './components';
import { useNovaCredentialsModal } from './hooks';
import { StyledCredentialsButton } from './styled';

import './account-credentials-button.scss';

interface Props {
  account: {
    tsAccount: string;
    tradingSystem: TradingSystem;
    tsUserId: string;
    tsAccountId: string;
  };
  disabled?: boolean;
  useNovaModal?: boolean;
}

const AccountCredentialsButton: FunctionComponent<Props> = ({
  account: { tsAccountId, tsAccount, tradingSystem, tsUserId },
  disabled,
  useNovaModal = false,
}) => {
  const {
    dataStore: { userStore },
  } = useStore();
  const { t } = useTranslation(['common', 'dashboardTemplates']);

  // Legacy Antd modal
  const { modal } = App.useApp();
  // Modular Nova UI modal
  const novaModal = useNovaCredentialsModal();
  const { getColor } = useColor();
  const { isDesktop, isSmallScreen } = useResponsiveness();
  const displayName = tsAccount || tsAccountId;
  const displayAccountName = '#' + displayName.split('accountName').join('');

  const isContest = isContestByTradingSystem(tradingSystem);

  const hasImpersonationAccess = userStore.hasImpersonationAccess({
    roles: CredentialsImpersonationRoles,
  });

  if (!hasImpersonationAccess) {
    return null;
  }

  // Legacy Antd modal content
  const getLegacyModalContent = () => {
    return (
      <TradersLogin
        userId={tsUserId || tsAccountId}
        tradingSystem={tradingSystem}
        accountId={tsAccountId}
        contest={isContest}
      />
    );
  };

  // Legacy Antd modal function (preserved for backward compatibility)
  const openLegacyTradersLoginModal = () => {
    modal.info({
      okText: t('common:ok'),
      maskClosable: true,
      icon: null,
      closable: true,
      title: (
        <Flex align="center" gap={8}>
          <TextComponent level={3} color={getColor(ThemeColors.colorPrimary)}>
            <Icons iconName={IconsNames.Lock} />
          </TextComponent>
          <TextComponent color={getColor(ThemeColors.colorPrimary)}>
            {displayAccountName}
          </TextComponent>
          <TextComponent weight={600}>
            {t('dashboardTemplates:account_overview.account_credentials')}
          </TextComponent>
        </Flex>
      ),
      content: getLegacyModalContent(),
    });
  };

  const openTradersLoginModal = () => {
    if (useNovaModal) {
      novaModal.openModal();
    } else {
      openLegacyTradersLoginModal();
    }
  };

  const renderNovaModal = () => {
    if (!useNovaModal) return null;

    return (
      <NovaCredentialsModal
        isOpen={novaModal.isOpen}
        onClose={novaModal.closeModal}
        displayAccountName={displayAccountName}
        userId={tsUserId || tsAccountId}
        tradingSystem={tradingSystem}
        accountId={tsAccountId}
        contest={isContest}
      />
    );
  };

  // Render StyledCredentialsButton for Nova UI modal (all screens)
  if (useNovaModal) {
    return (
      <>
        <StyledCredentialsButton
          onClick={disabled ? undefined : openTradersLoginModal}
          disabled={disabled}
        >
          <SystemIcon iconName={'Key'} />
        </StyledCredentialsButton>
        {renderNovaModal()}
      </>
    );
  }

  // Legacy rendering logic for non-Nova modal
  if (isDesktop) {
    return (
      <ThemeWrapper themeColor={ThemeColors.colorAttention}>
        <Button
          type="default"
          onClick={openTradersLoginModal}
          disabled={disabled}
          icon={
            <Icons
              color={getColor(ThemeColors.colorSecondary)}
              iconName={IconsNames.Password}
            />
          }
        >
          {t('common:credentials')}
        </Button>
      </ThemeWrapper>
    );
  }

  return (
    !disabled && (
      <Flex
        onClick={openTradersLoginModal}
        justify="center"
        align="center"
        className={classNames('account-credentials-float-button', {
          'account-credentials-float-button--smallScreen': isSmallScreen,
        })}
      >
        <Icons iconName={IconsNames.Password} />
      </Flex>
    )
  );
};
export default observer(
  withImpersonation(AccountCredentialsButton, CredentialsImpersonationRoles),
);
